import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SectionContainer = styled.section`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4rem 2rem;
  z-index: 2;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 3rem;
  color: white;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const CountdownContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 4rem;
  
  @media (max-width: 600px) {
    gap: 1rem;
  }
`;

const CountdownItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: rgba(10, 10, 15, 0.5);
  border: 1px solid rgba(160, 32, 240, 0.3);
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 20px rgba(160, 32, 240, 0.2);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      45deg,
      rgba(0, 191, 255, 0.1),
      rgba(160, 32, 240, 0.1)
    );
    z-index: -1;
  }
  
  @media (max-width: 600px) {
    width: 80px;
    height: 80px;
  }
`;

const CountdownNumber = styled.div`
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 700;
  color: white;
  text-shadow: 0 0 10px rgba(160, 32, 240, 0.5);
`;

const CountdownLabel = styled.div`
  font-size: 0.9rem;
  margin-top: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const EmailContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 500px;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  background: rgba(10, 10, 15, 0.5);
  border: 1px solid rgba(160, 32, 240, 0.3);
  border-radius: 30px;
  color: white;
  font-size: 1rem;
  outline: none;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:focus {
    border-color: rgba(0, 191, 255, 0.6);
    box-shadow: 0 0 15px rgba(0, 191, 255, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled(motion.button)`
  padding: 1rem 2.5rem;
  background: linear-gradient(90deg, #00BFFF, #A020F0);
  border: none;
  border-radius: 30px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 5px 15px rgba(160, 32, 240, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(160, 32, 240, 0.4);
  }
`;

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownSection: React.FC = () => {
  // Set launch date to 30 days from now
  const calculateLaunchDate = () => {
    const now = new Date();
    return new Date(now.setDate(now.getDate() + 30));
  };
  
  const [launchDate] = useState(calculateLaunchDate());
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +launchDate - +new Date();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(timer);
  }, [launchDate]);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };
  
  const formVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.4,
        ease: 'easeOut',
      },
    },
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would send the email to your server
    alert('Thank you for subscribing!');
  };
  
  return (
    <SectionContainer>
      <Title
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        Launching Soon
      </Title>
      
      <CountdownContainer
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <CountdownItem variants={itemVariants}>
          <CountdownNumber>{timeLeft.days}</CountdownNumber>
          <CountdownLabel>Days</CountdownLabel>
        </CountdownItem>
        
        <CountdownItem variants={itemVariants}>
          <CountdownNumber>{timeLeft.hours}</CountdownNumber>
          <CountdownLabel>Hours</CountdownLabel>
        </CountdownItem>
        
        <CountdownItem variants={itemVariants}>
          <CountdownNumber>{timeLeft.minutes}</CountdownNumber>
          <CountdownLabel>Minutes</CountdownLabel>
        </CountdownItem>
        
        <CountdownItem variants={itemVariants}>
          <CountdownNumber>{timeLeft.seconds}</CountdownNumber>
          <CountdownLabel>Seconds</CountdownLabel>
        </CountdownItem>
      </CountdownContainer>
      
      <EmailContainer
        variants={formVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <EmailInput
            type="email"
            placeholder="Enter your email for updates"
            required
          />
          <SubmitButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
          >
            Notify Me
          </SubmitButton>
        </form>
      </EmailContainer>
    </SectionContainer>
  );
};

export default CountdownSection; 